import { AppBreadcrumb, AppCard, AppSpace } from '@/components/atoms';
import { useCreateClinicLocationMutation } from '@/hooks/admin/useClinicLocationCreate';
import { useClinicOptionsQuery } from '@/hooks/admin/useClinicOptionsQuery';
import { useHealthCenterRegistTypes } from '@/hooks/admin/useHealthCenterRegistTypes';
import { useSiteAdminClinicDetail } from '@/hooks/admin/useSiteAdminClinicDetail';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CreateClinicLocationForm } from './CreateClinicLocationForm';
import type { ClinicLocationFormData } from './form-schema';

export default function CreateClinicLocationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { site } = useParams<{ site: string }>();
  const [searchParams] = useSearchParams();
  const { currentSite } = useSiteAdminAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteType = site ?? currentSite ?? 'medix';
  const clinicIdParam = searchParams.get('clinic_id');
  const preselectedClinicId =
    clinicIdParam != null && clinicIdParam !== '' ? Number(clinicIdParam) : null;

  const state = location.state as { clinicNumber?: string } | null;
  const clinicNumberFromState = state?.clinicNumber;

  const createMutation = useCreateClinicLocationMutation();
  const [clinicIdResetTrigger] = useState(0);

  /** Detail has correct service_type when options list is cached/stale. */
  const clinicDetailQuery = useSiteAdminClinicDetail(
    preselectedClinicId != null ? String(preselectedClinicId) : '',
    preselectedClinicId != null ? { staleTime: 0, refetchOnMount: 'always' } : undefined
  );
  const clinicNumberFromApi = clinicDetailQuery.data?.data?.clinic_number;

  const { data: clinicOptions } = useClinicOptionsQuery(siteType, {
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const clinics = useMemo(() => {
    const base = (clinicOptions ?? []).map(c => ({
      id: c.id,
      clinic_number: c.clinic_number,
      service_type: c.service_type ?? null,
    }));

    const detail = clinicDetailQuery.data?.data;
    if (
      preselectedClinicId == null ||
      !detail ||
      Number(detail.id) !== preselectedClinicId
    ) {
      return base;
    }

    const st = detail.service_type ?? null;
    const idx = base.findIndex(c => Number(c.id) === preselectedClinicId);
    if (idx >= 0) {
      const next = [...base];
      next[idx] = {
        ...next[idx],
        clinic_number: detail.clinic_number ?? next[idx].clinic_number,
        service_type: st ?? next[idx].service_type,
      };
      return next;
    }

    return [
      ...base,
      {
        id: preselectedClinicId,
        clinic_number: detail.clinic_number,
        service_type: st,
      },
    ];
  }, [clinicOptions, preselectedClinicId, clinicDetailQuery.data?.data]);

  const { data: healthCenterRegistTypesData } = useHealthCenterRegistTypes(siteType);
  const healthCenterRegistTypes = healthCenterRegistTypesData ?? [];
  const clinicNumberDisplay =
    clinicNumberFromState ??
    clinicNumberFromApi ??
    (preselectedClinicId != null ? String(preselectedClinicId) : null);

  const breadcrumbItems = useMemo(
    () => [{ title: '拠点', href: `/${siteType}/admin/clinic_locations` }, { title: '新規' }],
    [siteType]
  );

  const handleSubmit = async (data: ClinicLocationFormData) => {
    try {
      const payload = { ...data, is_compnay: data.is_compnay ?? null };
      const res = await createMutation.mutateAsync({
        siteType,
        payload,
      });
      const newLocationId = res?.data?.id;
      if (newLocationId != null) {
        navigate(`/${siteType}/admin/clinic_locations/${newLocationId}`, { replace: true });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancel = () => {
    if (clinicIdParam) {
      navigate(`/${siteType}/admin/clinics/${clinicIdParam}`);
    } else {
      navigate(`/${siteType}/admin/clinics`);
    }
  };

  const showBackToDetailButton =
    preselectedClinicId != null &&
    (clinics.some(c => Number(c.id) === preselectedClinicId) || clinicDetailQuery.isSuccess);

  return (
    <div className="p-6">
      <AppSpace orientation="vertical" className="w-full" size="large">
        <AppBreadcrumb items={breadcrumbItems} />

        <h1 className="text-xl font-semibold">拠点作成</h1>

        <AppCard className="w-full">
          <CreateClinicLocationForm
            clinics={clinics}
            healthCenterRegistTypes={healthCenterRegistTypes}
            preselectedClinicId={preselectedClinicId}
            clinicIdFromUrl={preselectedClinicId}
            clinicNumberDisplay={clinicNumberDisplay}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={createMutation.isPending}
            siteType={siteType}
            clinicIdResetTrigger={clinicIdResetTrigger}
            showBackToDetailButton={showBackToDetailButton}
          />
        </AppCard>
      </AppSpace>
    </div>
  );
}
